import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { theme } from '../styles/theme';
import { quoteRequestApi, productConfigApi, productApi, assetUrl } from '../api';
import { SelectedOption, Product, ProductImage, Product3dModel } from '../types';
import { formatOptionName, formatPrice } from '../utils/format';
import { DEFAULT_LANG, Lang } from '../config/locale';
import Header from '../components/Header';
import StepProgress from '../components/StepProgress';
import QuotePdfTemplate from '../components/QuotePdfTemplate';
import Model3DViewer from '../components/Model3DViewer';

type Slide =
  | { kind: '3d'; url: string; fileType: string; label: string }
  | { kind: 'image'; url: string; label: string };

interface FinalPageProps {
  selections: SelectedOption[];
  totalPrice: number;
  onReset: () => void;
  onLogoClick?: () => void;
  onGoToStep?: (stepIndex: number) => void;
  lang?: Lang;
  onLangChange?: (lang: Lang) => void;
}

const FinalPage: React.FC<FinalPageProps> = ({ selections, totalPrice, onReset, onLogoClick, onGoToStep, lang = DEFAULT_LANG, onLangChange }) => {
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const isKo = lang === 'ko';

  const [activeThumb, setActiveThumb] = useState(0);
  const [matchedProduct, setMatchedProduct] = useState<Product | null>(null);
  const [productImages, setProductImages] = useState<ProductImage[]>([]);
  const [productModels, setProductModels] = useState<Product3dModel[]>([]);
  const [quoteNumber] = useState(() => {
    const now = new Date();
    const rand = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
    return `WS-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${rand}`;
  });
  const steps = selections.map((s) => s.step);

  // 가격은 매칭된 최종 제품의 base_price 단일가만 사용 (옵션별 합산 폐기)
  const modelPrice = matchedProduct?.base_price ?? 0;
  const hasPrice = modelPrice > 0;
  // base_price가 0이거나 매칭 제품이 아직 없으면 '가격 문의' 표시
  const priceLabel = hasPrice
    ? formatPrice(modelPrice, lang)
    : (isKo ? '가격 문의' : 'Contact for price');

  // 갤러리용 옵션 이미지: 이미지 있는 것만 + 같은 이미지(URL) 중복 제거.
  // 동력방식/동력원 단계는 하위 단계(모터/브랜드)와 같은 대표 이미지를 빌려 쓰므로 갤러리에선 제외(선택 카드엔 유지).
  const GALLERY_EXCLUDE_STEPS = new Set(['power_type', 'power_source']);
  const galleryImages = (() => {
    const seen = new Set<string>();
    const list: { url: string; name: string }[] = [];
    for (const sel of selections) {
      if (GALLERY_EXCLUDE_STEPS.has(sel.step.step_key)) continue;
      const u = sel.option.image_url;
      if (u && !seen.has(u)) {
        seen.add(u);
        list.push({
          url: u,
          name: formatOptionName(isKo ? sel.option.option_name : (sel.option.option_name_en || sel.option.option_name)),
        });
      }
    }
    return list;
  })();
  // 통합 슬라이드: [ 3D 모델들, 제품 이미지들, 옵션 이미지들 ]
  const slides = useMemo<Slide[]>(() => {
    const list: Slide[] = [];
    // 1) 3D 모델
    for (const m of productModels) {
      list.push({
        kind: '3d',
        url: assetUrl(m.model_url),
        fileType: (m.file_type || '').toLowerCase(),
        label: isKo ? '3D 모델' : '3D Model',
      });
    }
    // 2) 제품 대표 이미지
    for (const img of productImages) {
      list.push({
        kind: 'image',
        url: assetUrl(img.image_url),
        label: matchedProduct?.model_name || '',
      });
    }
    // 3) 옵션 이미지
    for (const g of galleryImages) {
      list.push({
        kind: 'image',
        url: assetUrl(g.url),
        label: g.name,
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productModels, productImages, matchedProduct?.model_name, JSON.stringify(galleryImages)]);

  const activeSlide = slides[activeThumb];
  const activeLabel = activeSlide?.label || '';
  // 이미지 확대 모달용 현재 이미지 URL (이미지 슬라이드일 때만)
  const currentImageSrc = activeSlide?.kind === 'image' ? activeSlide.url : '';

  // 이미지 확대 모달(줌/팬)
  const [modalOpen, setModalOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  // 3D 전체화면 라이트박스
  const [model3dOpen, setModel3dOpen] = useState(false);
  const openMainSlide = () => {
    if (!activeSlide) return;
    if (activeSlide.kind === '3d') { setModel3dOpen(true); return; }
    if (currentImageSrc) { setZoom(1); setPan({ x: 0, y: 0 }); setModalOpen(true); }
  };
  const openModal = () => { if (currentImageSrc) { setZoom(1); setPan({ x: 0, y: 0 }); setModalOpen(true); } };
  const closeModal = () => { setModalOpen(false); dragRef.current = null; };
  const zoomIn = () => setZoom((z) => Math.min(Math.round((z + 0.5) * 10) / 10, 4));
  const zoomOut = () => setZoom((z) => { const nz = Math.max(Math.round((z - 0.5) * 10) / 10, 1); if (nz === 1) setPan({ x: 0, y: 0 }); return nz; });
  const resetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }); };
  const onModalWheel = (e: React.WheelEvent) => {
    setZoom((z) => {
      const nz = Math.min(Math.max(Math.round((z + (e.deltaY < 0 ? 0.2 : -0.2)) * 10) / 10, 1), 4);
      if (nz === 1) setPan({ x: 0, y: 0 });
      return nz;
    });
  };
  const onDragStart = (e: React.MouseEvent) => { if (zoom > 1) { dragRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }; } };
  const onDragMove = (e: React.MouseEvent) => { if (dragRef.current) { setPan({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y }); } };
  const onDragEnd = () => { dragRef.current = null; };

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalOpen(false);
      else if (e.key === '+' || e.key === '=') zoomIn();
      else if (e.key === '-') zoomOut();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!model3dOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setModel3dOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [model3dOpen]);

  // 모달이 열려 있는 동안 뒤쪽(견적서) 페이지 스크롤 잠금
  useEffect(() => {
    if (!modalOpen && !model3dOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [modalOpen, model3dOpen]);

  // 선택된 옵션 조합으로 제품 매칭
  useEffect(() => {
    if (selections.length === 0) return;
    const optionIds = selections.map((s) => s.option.id);
    productConfigApi.match(optionIds)
      .then((res) => {
        const configs = Array.isArray(res.data) ? res.data : [];
        if (configs.length > 0 && configs[0].product) {
          setMatchedProduct(configs[0].product);
        }
      })
      .catch(() => {
        // 매칭 실패 시 무시 (기본 URL 사용)
      });
  }, [selections]);

  // 매칭된 제품의 2D 이미지 로드 (갤러리 표시용)
  useEffect(() => {
    if (!matchedProduct?.id) { setProductImages([]); setProductModels([]); return; }
    productApi.getOne(matchedProduct.id)
      .then((res) => {
        const imgs = (res.data?.images || []).filter((im: ProductImage) => im.image_url);
        setProductImages(imgs);
        setProductModels((res.data?.models_3d || []).filter((m: Product3dModel) => m.model_url));
        setActiveThumb(0);
      })
      .catch(() => { setProductImages([]); setProductModels([]); });
  }, [matchedProduct?.id]);

  const pdfRef = useRef<HTMLDivElement>(null);
  const [sending, setSending] = useState(false);
  const [sendingStep, setSendingStep] = useState('');

  // PDF Blob 생성 공통 함수
  const generatePdfBlob = useCallback(async (): Promise<Blob | null> => {
    if (!pdfRef.current) return null;
    pdfRef.current.style.display = 'block';
    const canvas = await html2canvas(pdfRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#FFFFFF',
    });
    pdfRef.current.style.display = 'none';

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pdfWidth, pdfHeight);
    return pdf.output('blob');
  }, []);

  const handlePdfSave = useCallback(async () => {
    try {
      const blob = await generatePdfBlob();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${quoteNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF 저장 실패:', err);
    }
  }, [generatePdfBlob, quoteNumber]);

  const handleSendQuote = async () => {
    if (!email || sending) return;
    setSending(true);
    try {
      setSendingStep(isKo ? '견적서 생성 중...' : 'Generating quote...');
      const blob = await generatePdfBlob();
      if (!blob) throw new Error('PDF 생성 실패');

      setSendingStep(isKo ? '이메일 전송 중...' : 'Sending email...');
      const formData = new FormData();
      formData.append('pdf', blob, `${quoteNumber}.pdf`);
      formData.append('email', email);
      formData.append('quote_number', quoteNumber);
      formData.append('lang', isKo ? 'ko' : 'en');
      formData.append('selected_options', JSON.stringify(
        selections.map((s) => ({
          step: isKo ? s.step.step_name : (s.step.step_name_en || s.step.step_name),
          option: isKo ? s.option.option_name : (s.option.option_name_en || s.option.option_name),
        })),
      ));
      formData.append('total_price', String(modelPrice));
      if (matchedProduct?.id) {
        formData.append('product_id', String(matchedProduct.id));
      }

      await quoteRequestApi.send_quote(formData);
      setEmailSent(true);
    } catch (err) {
      alert(isKo ? '견적 전송에 실패했습니다. 다시 시도해주세요.' : 'Failed to send quote. Please try again.');
    } finally {
      setSending(false);
      setSendingStep('');
    }
  };

  // 2026-09-18 영문 사이트는 견적 전용으로 바뀌어 구매 버튼을 뺐다. 한국어 화면에만 남긴다.
  // 쇼핑몰 홈 (모델별 구매 URL이 없을 때 폴백)
  const STORE_HOME = 'https://xn--z69azfl4br08b28au7o.com/';
  // 매칭된 모델의 구매 페이지(smartstore_url)가 있으면 그곳으로, 없으면 홈으로
  const purchaseUrl = matchedProduct?.smartstore_url || STORE_HOME;
  const handlePurchase = () => {
    window.open(purchaseUrl, '_blank');
  };

  return (
    <Wrapper>
      <Header
        totalPrice={modelPrice}
        onResetClick={onReset}
        onLogoClick={onLogoClick}
        showReset
        showPrice
        isFinalPage
        lang={lang}
        onLangChange={onLangChange}
      />
      <QuotePdfTemplate ref={pdfRef} selections={selections} totalPrice={modelPrice} quoteNumber={quoteNumber} modelName={matchedProduct?.model_name} isKo={isKo} />
      <Content>
        <StepProgress
          steps={steps}
          currentIndex={steps.length}
          completedCount={steps.length}
          onStepClick={onGoToStep}
          lang={lang}
        />

        <MainArea>
          <GallerySection>
            <MainImage
              onClick={activeSlide?.kind === '3d' ? undefined : openMainSlide}
              $zoomable={!!activeSlide && activeSlide.kind !== '3d'}
            >
              {activeSlide ? (
                activeSlide.kind === '3d'
                  ? <Model3DSlot><Model3DViewer url={activeSlide.url} fileType={activeSlide.fileType} /></Model3DSlot>
                  : <MainImg src={activeSlide.url} alt={activeSlide.label} />
              ) : (
                <ThreeDLabel>3D</ThreeDLabel>
              )}
              {activeSlide?.kind === '3d' && (
                <ZoomButton type="button" onClick={() => setModel3dOpen(true)} title={isKo ? '크게 보기' : 'Enlarge'}>
                  ⛶
                </ZoomButton>
              )}
              {activeSlide?.kind === 'image' && <ZoomHint>🔍</ZoomHint>}
              {activeLabel && <ImageLabel>{activeLabel}</ImageLabel>}
            </MainImage>
            <ThumbnailGrid $count={slides.length || 1}>
              {slides.length === 0 && (
                <Thumbnail $active title={isKo ? '3D 렌더링' : '3D Rendering'}>
                  <ThreeDLabel>3D</ThreeDLabel>
                </Thumbnail>
              )}
              {slides.map((s, idx) => (
                <Thumbnail
                  key={`${s.kind}-${idx}-${s.url}`}
                  $active={activeThumb === idx}
                  onClick={() => setActiveThumb(idx)}
                  title={s.label}
                >
                  {s.kind === '3d' ? (
                    <ThreeDThumb>
                      <CubeIcon viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2.5L21 7v10l-9 4.5L3 17V7l9-4.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                        <path d="M3 7l9 4.5L21 7M12 11.5V21.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                      </CubeIcon>
                      <ThreeDBadge>3D</ThreeDBadge>
                    </ThreeDThumb>
                  ) : (
                    <ThumbImg src={s.url} alt={s.label} loading="lazy" />
                  )}
                </Thumbnail>
              ))}
            </ThumbnailGrid>

            {/* ③ 상담/연락처 안내 (좌측 하단) */}
            <ContactBox>
              <ContactTitle>{isKo ? '견적·상담 문의' : 'Contact & Consultation'}</ContactTitle>
              <ContactGrid>
                <ContactItem><ContactLabel>{isKo ? '고객센터' : 'Call'}</ContactLabel><ContactValue>{isKo ? '1522-1609' : '+82-1522-1609'}</ContactValue></ContactItem>
                <ContactItem><ContactLabel>{isKo ? '휴대폰' : 'Mobile'}</ContactLabel><ContactValue>{isKo ? '010-3245-3571' : '+82-10-3245-3571'}</ContactValue></ContactItem>
                <ContactItem><ContactLabel>{isKo ? '카카오톡' : 'KakaoTalk'}</ContactLabel><ContactValue>top094989</ContactValue></ContactItem>
                <ContactItem><ContactLabel>{isKo ? '상담시간' : 'Hours'}</ContactLabel><ContactValue>{isKo ? '월~토 08:00~20:00' : 'Mon–Sat 08:00–20:00'}</ContactValue></ContactItem>
              </ContactGrid>
              <ContactNotice>{isKo ? '본 견적은 참고용이며, 실제 구매 가격은 상담 후 확정됩니다.' : 'This quote is for reference; final price is confirmed after consultation.'}</ContactNotice>
            </ContactBox>
          </GallerySection>

          <SummarySection>
            <SummaryCard>
              <SummaryTitle>
                <span>{isKo ? '최종 견적 요약' : 'Quote Summary'}</span>
              </SummaryTitle>
              <SummaryDesc>{isKo ? '선택하신 옵션이 반영된 맞춤 구성입니다.' : 'Your customized configuration based on selected options.'}</SummaryDesc>

              {/* ② 모델 정보 헤더 */}
              {matchedProduct && (
                <ModelHeader>
                  <ModelHeaderTop>
                    <ModelHeaderName>{matchedProduct.model_name}</ModelHeaderName>
                  </ModelHeaderTop>
                  <ModelMeta>
                    <span>{isKo ? '견적번호' : 'Quote No.'} · {quoteNumber}</span>
                    <span>{isKo ? '유효기간 발행일로부터 30일' : 'Valid for 30 days'}</span>
                  </ModelMeta>
                </ModelHeader>
              )}

              {/* ① 단계명(구분) + 선택값 */}
              <OptionList>
                {selections.map((sel, idx) => (
                  <OptionRow key={sel.step.id}>
                    <OptionIndex>{String(idx + 1).padStart(2, '0')}</OptionIndex>
                    <OptionStep>{isKo ? sel.step.step_name : (sel.step.step_name_en || sel.step.step_name)}</OptionStep>
                    <OptionName>{formatOptionName(isKo ? sel.option.option_name : (sel.option.option_name_en || sel.option.option_name))}</OptionName>
                  </OptionRow>
                ))}
              </OptionList>

              <TotalBadge>
                <TotalLabel>{isKo ? '최종 금액 (VAT 포함)' : 'Total (incl. VAT)'}</TotalLabel>
                <TotalPrice>{priceLabel}</TotalPrice>
              </TotalBadge>

              <EmailSection>
                <EmailLabel>
                  <EmailIcon viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 0H2C0.9 0 0.01 0.9 0.01 2L0 14C0 15.1 0.9 16 2 16H18C19.1 16 20 15.1 20 14V2C20 0.9 19.1 0 18 0ZM18 4L10 9L2 4V2L10 7L18 2V4Z" fill="currentColor"/>
                  </EmailIcon>
                  {isKo ? '이메일로 견적서 받기' : 'Get Quote via Email'}
                </EmailLabel>
                <EmailRow>
                  <EmailInput
                    type="email"
                    placeholder={isKo ? '이메일 주소를 입력하세요' : 'Enter your email address'}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={emailSent || sending}
                  />
                  <EmailButton onClick={handleSendQuote} disabled={emailSent || !email || sending}>
                    {emailSent
                      ? (isKo ? '전송 완료 ✓' : 'Sent ✓')
                      : sending
                        ? (isKo ? '전송 중...' : 'Sending...')
                        : (isKo ? '전송하기' : 'Send')}
                  </EmailButton>
                  <PdfSaveButton onClick={handlePdfSave} title={isKo ? 'PDF로 저장하기' : 'Save as PDF'}>
                    <SaveIcon viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 2.5V12M10 12L6.5 8.5M10 12L13.5 8.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3.5 13V15.5C3.5 16.33 4.17 17 5 17H15C15.83 17 16.5 16.33 16.5 15.5V13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                    </SaveIcon>
                    PDF
                  </PdfSaveButton>
                </EmailRow>
                {sending && <SendingStatus>{sendingStep}</SendingStatus>}
                {emailSent && (
                  <SendingStatus $success>
                    {isKo ? '견적서가 접수되었습니다. 잠시 후 이메일로 발송됩니다.' : 'Your quote has been received. It will be sent to your email shortly.'}
                  </SendingStatus>
                )}
              </EmailSection>

              {isKo && (
                <PurchaseButton onClick={handlePurchase}>
                  구매하러 가기
                </PurchaseButton>
              )}
            </SummaryCard>
          </SummarySection>
        </MainArea>
      </Content>

      {modalOpen && currentImageSrc && (
        <ModalOverlay onClick={closeModal} onMouseMove={onDragMove} onMouseUp={onDragEnd} onMouseLeave={onDragEnd}>
          <ModalToolbar onClick={(e) => e.stopPropagation()}>
            <ToolbarBtn onClick={zoomOut} title={isKo ? '축소' : 'Zoom out'}>−</ToolbarBtn>
            <ZoomPct>{Math.round(zoom * 100)}%</ZoomPct>
            <ToolbarBtn onClick={zoomIn} title={isKo ? '확대' : 'Zoom in'}>＋</ToolbarBtn>
            <ToolbarBtn onClick={resetZoom} title={isKo ? '원본' : 'Reset'}>⟲</ToolbarBtn>
            <ToolbarBtn onClick={closeModal} title={isKo ? '닫기' : 'Close'}>✕</ToolbarBtn>
          </ModalToolbar>
          <ModalImg
            src={currentImageSrc}
            alt={activeLabel}
            draggable={false}
            onClick={(e) => e.stopPropagation()}
            onWheel={onModalWheel}
            onMouseDown={onDragStart}
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              cursor: zoom > 1 ? (dragRef.current ? 'grabbing' : 'grab') : 'default',
            }}
          />
          {activeLabel && <ModalCaption onClick={(e) => e.stopPropagation()}>{activeLabel}</ModalCaption>}
        </ModalOverlay>
      )}

      {model3dOpen && activeSlide?.kind === '3d' && (
        <ModalOverlay onClick={() => setModel3dOpen(false)}>
          <ModalToolbar onClick={(e) => e.stopPropagation()}>
            <ToolbarBtn onClick={() => setModel3dOpen(false)} title={isKo ? '닫기' : 'Close'}>✕</ToolbarBtn>
          </ModalToolbar>
          <Model3DStage onClick={(e) => e.stopPropagation()}>
            <Model3DViewer url={activeSlide.url} fileType={activeSlide.fileType} />
          </Model3DStage>
          <ModalCaption onClick={(e) => e.stopPropagation()}>
            {isKo ? '드래그하여 회전 · 스크롤로 확대' : 'Drag to rotate · Scroll to zoom'}
          </ModalCaption>
        </ModalOverlay>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  min-height: 100vh;
  background: ${theme.colors.background};
`;

const Content = styled.div`
  max-width: ${theme.layout.maxWidth};
  margin: 0 auto;
  padding: 0 40px 60px;
`;

const MainArea = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-top: 20px;
`;

const GallerySection = styled.div`
  display: flex;
  flex-direction: column;
`;

const MainImage = styled.div<{ $zoomable?: boolean }>`
  width: 100%;
  aspect-ratio: 4 / 3;
  background: ${theme.colors.white};
  border-radius: ${theme.layout.cardRadius};
  border: 1px solid ${theme.colors.border};
  margin-bottom: 16px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: ${({ $zoomable }) => ($zoomable ? 'zoom-in' : 'default')};
`;

const ZoomHint = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  pointer-events: none;
`;

// 3D 뷰어 전용 "크게 보기" 버튼 (드래그 회전과 분리하기 위해 컨테이너 onClick 대신 이 버튼으로만 모달 오픈)
const ZoomButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  z-index: 2;
  transition: background 0.15s;
  &:hover { background: rgba(0, 0, 0, 0.7); }
`;

const ImageLabel = styled.div`
  position: absolute;
  right: 16px;
  bottom: 16px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 7px 16px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  pointer-events: none;
`;

const MainImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 24px;
`;

const ThumbImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 6px;
`;

const ThumbnailGrid = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $count }) => Math.min($count, 6)}, 1fr);
  gap: 12px;
`;

const Thumbnail = styled.div<{ $active?: boolean }>`
  aspect-ratio: 1;
  background: ${theme.colors.white};
  border-radius: 8px;
  border: 2px solid ${({ $active }) => $active ? theme.colors.primary : theme.colors.border};
  cursor: pointer;
  position: relative;
  transition: border-color 0.2s;
  &:hover { border-color: ${theme.colors.primary}; }
`;

const ThreeDLabel = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: ${theme.colors.primary};
`;

const Model3DSlot = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const ThreeDThumb = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: ${theme.colors.primary};
  background: ${theme.colors.primaryLight};
`;

const CubeIcon = styled.svg`
  width: 26px;
  height: 26px;
`;

const ThreeDBadge = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: ${theme.colors.primaryDark};
`;

const Model3DStage = styled.div`
  width: 86vw;
  height: 82vh;
  max-width: 1100px;
  border-radius: 12px;
  overflow: hidden;
  background: #f8fafc;
`;

const SummarySection = styled.div``;

const SummaryCard = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.layout.cardRadius};
  padding: 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
`;

const SummaryTitle = styled.h2`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 24px;
  font-weight: 700;
  color: ${theme.colors.textPrimary};
  margin-bottom: 8px;
`;

const SummaryDesc = styled.p`
  font-size: 14px;
  color: ${theme.colors.textSecondary};
  margin-bottom: 28px;
`;

const ModelHeader = styled.div`
  background: ${theme.colors.primaryLight};
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 24px;
`;

const ModelHeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ModelHeaderName = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: ${theme.colors.primaryDark};
`;

const ModelMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: 10px;
  font-size: 12px;
  color: ${theme.colors.textTertiary};
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  border-top: 1px solid ${theme.colors.border};
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 2px;
  border-bottom: 1px solid ${theme.colors.border};
`;

const OptionIndex = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${theme.colors.primary};
  min-width: 22px;
`;

const OptionStep = styled.span`
  flex: 1;
  font-size: 13px;
  color: ${theme.colors.textTertiary};
`;

const OptionName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${theme.colors.textPrimary};
  text-align: right;
`;

const TotalBadge = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${theme.colors.primary};
  border-radius: 12px;
  padding: 16px 24px;
  margin-bottom: 28px;
`;

const TotalLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${theme.colors.white};
`;

const TotalPrice = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: ${theme.colors.white};
`;

const EmailSection = styled.div`
  margin-bottom: 20px;
`;

const EmailLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.textPrimary};
  margin-bottom: 10px;
`;

const EmailIcon = styled.svg`
  width: 18px;
  height: 14px;
  color: ${theme.colors.textPrimary};
  flex-shrink: 0;
`;

const EmailRow = styled.div`
  display: flex;
  gap: 8px;
`;

const SendingStatus = styled.div<{ $success?: boolean }>`
  margin-top: 8px;
  font-size: 13px;
  color: ${({ $success }) => $success ? theme.colors.success : theme.colors.textSecondary};
  ${({ $success }) => !$success && `
    &::before {
      content: '';
      display: inline-block;
      width: 12px;
      height: 12px;
      border: 2px solid ${theme.colors.textTertiary};
      border-top-color: ${theme.colors.primary};
      border-radius: 50%;
      margin-right: 6px;
      vertical-align: middle;
      animation: spin 0.8s linear infinite;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `}
`;

const EmailInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  font-size: 14px;
  &:focus { border-color: ${theme.colors.primary}; }
  &:disabled { background: #f5f5f5; }
`;

const EmailButton = styled.button<{ disabled?: boolean }>`
  background: ${({ disabled }) => disabled ? theme.colors.stepperGray : theme.colors.textPrimary};
  color: ${theme.colors.white};
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: ${({ disabled }) => disabled ? 'none' : 'auto'};
`;

const PdfSaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${theme.colors.white};
  border: 1.5px solid ${theme.colors.primary};
  color: ${theme.colors.primaryDark};
  padding: 12px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: ${theme.colors.primaryLight}; }
`;

const SaveIcon = styled.svg`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const ContactBox = styled.div`
  margin-top: auto;
  padding: 16px 18px;
  background: #FAFAFA;
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
`;

const ContactTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${theme.colors.textPrimary};
  margin-bottom: 12px;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
`;

const ContactItem = styled.div`
  display: flex;
  gap: 8px;
  font-size: 12.5px;
`;

const ContactLabel = styled.span`
  color: ${theme.colors.textTertiary};
  min-width: 54px;
`;

const ContactValue = styled.span`
  color: ${theme.colors.textPrimary};
  font-weight: 600;
`;

const ContactNotice = styled.p`
  margin-top: 12px;
  font-size: 11.5px;
  color: ${theme.colors.textTertiary};
  line-height: 1.45;
`;

const PurchaseButton = styled.button`
  width: 100%;
  padding: 16px;
  background: ${theme.colors.success};
  color: ${theme.colors.white};
  font-size: 18px;
  font-weight: 600;
  border-radius: 12px;
  transition: background 0.2s;
  &:hover { background: ${theme.colors.successDark}; }
`;

// ===== 이미지 확대 모달 =====
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ModalImg = styled.img`
  max-width: 86vw;
  max-height: 82vh;
  object-fit: contain;
  user-select: none;
  transition: transform 0.05s linear;
  will-change: transform;
`;

const ModalToolbar = styled.div`
  position: fixed;
  top: 20px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.55);
  padding: 8px 12px;
  border-radius: 999px;
  z-index: 1001;
`;

const ToolbarBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  &:hover { background: rgba(255, 255, 255, 0.28); }
`;

const ZoomPct = styled.span`
  min-width: 48px;
  text-align: center;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
`;

const ModalCaption = styled.div`
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 8px 18px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
`;

export default FinalPage;
